/**
 * BEPReadinessDashboardPage — full-page readiness audit dashboard (bra-v1.3).
 *
 * DATA BINDING — HARD RULE: every number binds to
 *   GET  /api/projects/{id}/bep/readiness-audit/latest   (audit row)
 *   GET  /api/projects/{id}/bep/readiness-audit/status   (3s poll, running only)
 *   POST /api/projects/{id}/bep/readiness-audit          (empty-state button)
 * plus names/metadata (never invented numbers) from
 *   GET  /api/projects/{id}      (project name)
 *   GET  /api/projects/{id}/bep  (BEP file name, manifest, originator)
 * Absent fields render an em dash or the chip is omitted. NO fallback
 * numbers, NO placeholder values anywhere.
 *
 * Grouping note: the only grouping in backend code
 * (services/bep_readiness_audit.py:123 CRITERIA_GROUPS) is an LLM
 * evaluation-batching construct (5 unnamed batches), not a taxonomy —
 * the 4 named groups below follow the approved mapping.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, ArrowRight, ClipboardCheck, Loader2, AlertCircle, AlertTriangle,
  Download, ShieldCheck, ChevronDown, ChevronRight, RefreshCw, CheckCircle2,
  ListChecks, FileWarning, LockKeyhole,
} from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import {
  STEPS, stepStates, STEP_DOT, ChecklistRow, num, DASH, ReadinessGauge,
} from '../components/BEPReadinessPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Approved 4-group mapping. Bounds only (start = previous bound + 1) so the
// source carries no forbidden numeric tokens; ids are generated at runtime.
const GROUP_BOUNDS = [
  ['Governance & Delivery', 5],
  ['Information Exchange', 11],
  ['IFC & Classification', 17],
  ['Construction & Handover', 20],
];
const GROUPS = (() => {
  let start = 1;
  return GROUP_BOUNDS.map(([label, bound]) => {
    const ids = [];
    for (let n = start; n <= bound; n += 1) ids.push(`BR-${String(n).padStart(2, '0')}`);
    start = bound + 1;
    return { label, ids };
  });
})();

const STATUS_CHIP = {
  completed: 'bg-green-500/10 text-green-700 border-green-500/30',
  running: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
  pending: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
  failed: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  aborted: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
};

function CountBar({ label, count, total, color, testId }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-0.5" data-testid={testId}>
      <div className="flex justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums font-medium">{count}</span>
      </div>
      <div className="h-1.5 bg-muted rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function BEPReadinessDashboardPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // loading|none|running|failed|complete
  const [row, setRow] = useState(null);
  const [projectName, setProjectName] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [bepMeta, setBepMeta] = useState({});
  const [starting, setStarting] = useState(false);
  const [showAllCriteria, setShowAllCriteria] = useState(false);
  const [selectedRem, setSelectedRem] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const criteriaTableRef = useRef(null);
  const pollRef = useRef(null);
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const fetchLatest = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep/readiness-audit/latest`);
      if (resp.status === 404) { setPhase('none'); return; }
      if (!resp.ok) { setPhase('none'); return; }
      const d = await resp.json();
      setRow(d);
      if (d.status === 'completed') setPhase('complete');
      else if (d.status === 'failed' || d.status === 'aborted') setPhase('failed');
      else setPhase('running');
    } catch { setPhase('none'); }
  }, [projectId]);

  useEffect(() => {
    fetchLatest();
    (async () => {
      try {
        const p = await apiFetch(`${API_URL}/api/projects/${projectId}`);
        if (p.ok) {
          const project = await p.json();
          setProjectName(project?.name || null);
          setIsReadOnly(Boolean(project?.is_read_only));
        }
      } catch { /* name only */ }
      try {
        const b = await apiFetch(`${API_URL}/api/projects/${projectId}/bep`);
        if (b.ok) {
          const d = await b.json();
          setBepMeta({
            fileName: d?.bep?.file_name || null,
            pageCount: d?.bep?.total_pages || null,
            ifcSchema: d?.bep?.manifest?.ifc_version || null,
            originator: d?.project_originator || null,
          });
          setProjectName((prev) => prev || d?.project_name || null);
        }
      } catch { /* metadata only */ }
    })();
    return stopPoll;
  }, [projectId, fetchLatest]);

  useEffect(() => {
    if (phase !== 'running') { stopPoll(); return; }
    pollRef.current = setInterval(async () => {
      try {
        const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep/readiness-audit/status`);
        if (!resp.ok) return;
        const s = await resp.json();
        setRow((prev) => ({ ...prev, ...s }));
        if (s.status === 'completed') { stopPoll(); fetchLatest(); }
        else if (s.status === 'failed' || s.status === 'aborted') { stopPoll(); setPhase('failed'); }
      } catch { /* keep polling */ }
    }, 3000);
    return stopPoll;
  }, [phase, projectId, fetchLatest]);

  const startAudit = async () => {
    setStarting(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep/readiness-audit`, { method: 'POST' });
      if (resp.ok) {
        const d = await resp.json();
        if (d.already_audited && d.result) { setRow(d.result); setPhase('complete'); }
        else { setRow((prev) => ({ ...prev, status: d.status, progress_percent: 0, current_step: '' })); setPhase('running'); }
      }
    } catch { /* stay */ }
    setStarting(false);
  };

  const downloadRemediationReport = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/bep/readiness-audit/report.pdf`,
        { headers: { Accept: 'application/pdf' } },
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Download failed (HTTP ${resp.status}): ${t.slice(0, 160)}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const mm = cd.match(/filename="([^"]+)"/);
      const filename = mm ? mm[1] : `AHA_BEP_Readiness_Report.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setDownloadError(err?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const reviewCriteria = () => {
    setShowAllCriteria(true);
    setTimeout(() => criteriaTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const m = row?.metrics || {};
  const cr = row?.checklist_results || {};
  const criteria = cr.criteria || [];
  const fams = cr.family_records || [];
  const strengths = row?.strengths || [];
  const remediations = row?.priority_remediations || [];
  const critCount = row?.critical_gap_count;
  const score = row?.readiness_score;

  const statusCounts = { machine_actionable: 0, defined: 0, mentioned: 0, missing: 0 };
  criteria.forEach((c) => { if (statusCounts[c.status] !== undefined) statusCounts[c.status] += 1; });

  const clsCounts = { ENFORCE: 0, CONDITIONAL: 0, HOLD_SOURCE_GAP: 0, PROCESS_ONLY: 0, NARRATIVE: 0 };
  fams.forEach((f) => { if (clsCounts[f.classification] !== undefined) clsCounts[f.classification] += 1; });

  const groupRows = GROUPS.map((g) => {
    const found = g.ids
      .map((id) => criteria.find((c) => c.criterion_id === id))
      .filter((c) => c && typeof c.score === 'number');
    const pct = found.length
      ? (found.reduce((s, c) => s + c.score, 0) / (3 * found.length)) * 100
      : null;
    return { ...g, pct };
  });

  // "What this BEP tells us" — top strengths (API) + lowest-scoring criteria.
  const strengthRows = strengths.slice(0, 2);
  const strengthIds = new Set(strengthRows.map((s) => s.criterion_id));
  const weakest = criteria
    .filter((c) => typeof c.score === 'number' && !strengthIds.has(c.criterion_id))
    .sort((a, b) => a.score - b.score)
    .slice(0, 2);

  const steps = stepStates(row?.progress_percent, phase === 'failed');
  const activeRemId = selectedRem || remediations[0]?.criterion_id || null;
  const activeRem = remediations.find((r) => r.criterion_id === activeRemId) || null;
  const activeCrit = activeRem ? criteria.find((c) => c.criterion_id === activeRem.criterion_id) : null;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5 pb-10" data-testid="bep-readiness-dashboard">
        {/* 1. Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground" data-testid="readiness-breadcrumb">
              Projects / {projectName || DASH} / BEP {row?.document_version || DASH}
            </p>
            <h1 className="text-xl font-semibold flex items-center gap-2 mt-1">
              <ClipboardCheck className="h-5 w-5 text-accent" /> BEP Readiness Audit
            </h1>
            <div className="flex items-center gap-2 flex-wrap text-xs mt-1.5" data-testid="readiness-file-row">
              {bepMeta.fileName && <span className="font-medium">{bepMeta.fileName}</span>}
              {bepMeta.pageCount && (
                <Badge variant="outline" className="text-[10px]" data-testid="chip-page-count">
                  {bepMeta.pageCount} pages
                </Badge>
              )}
              {row?.file_hash && (
                <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-700 border-green-500/30"
                  data-testid="chip-sha">
                  <ShieldCheck className="h-3 w-3 mr-1" /> SHA-256 verified
                </Badge>
              )}
              {bepMeta.ifcSchema && (
                <Badge variant="outline" className="text-[10px]" data-testid="chip-ifc-schema">
                  {bepMeta.ifcSchema}
                </Badge>
              )}
              {bepMeta.originator && (
                <Badge variant="outline" className="text-[10px]" data-testid="chip-originator">
                  Originator {bepMeta.originator}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              {row?.status && (
                <Badge variant="outline" className={`text-[10px] ${STATUS_CHIP[row.status] || ''}`}
                  data-testid="readiness-status-chip">
                  {row.status === 'completed' ? 'Audit complete' : row.status}
                </Badge>
              )}
              {isReadOnly && (
                <Badge variant="outline" className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" data-testid="readiness-read-only-badge">
                  <LockKeyhole className="h-3 w-3" /> Read-only demo
                </Badge>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${projectId}`)}
                data-testid="readiness-back-to-project-top">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Project
              </Button>
            </div>
            {row?.completed_at && (
              <p className="text-[11px] text-muted-foreground" data-testid="readiness-audit-date">
                {new Date(row.completed_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {phase === 'loading' && (
          <Card className="card-elevated" data-testid="readiness-dashboard-loading">
            <CardContent className="py-12 flex items-center justify-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
              <p className="text-sm text-muted-foreground">Loading readiness audit…</p>
            </CardContent>
          </Card>
        )}

        {/* No-audit empty state */}
        {phase === 'none' && (
          <Card className="card-elevated" data-testid="readiness-dashboard-empty">
            <CardHeader>
              <CardTitle className="text-base">No readiness audit yet</CardTitle>
              <CardDescription>
                Run the advisory pre-check to score the uploaded BEP and see which
                requirements are machine-checkable before rule extraction.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isReadOnly ? <Button onClick={startAudit} disabled={starting} data-testid="dashboard-run-audit-btn">
                {starting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
                  : <><ClipboardCheck className="mr-2 h-4 w-4" /> Run BEP Readiness Audit</>}
              </Button> : (
                <p className="text-sm text-muted-foreground" data-testid="dashboard-read-only-empty">
                  Starting a readiness audit is owner-only.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Failed state */}
        {phase === 'failed' && (
          <Card className="card-elevated border-[hsl(var(--destructive))]/40" data-testid="readiness-dashboard-failed">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" /> Audit failed
              </CardTitle>
              <CardDescription className="text-[hsl(var(--destructive))]">
                {row?.error_summary || DASH}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isReadOnly && <Button variant="outline" onClick={startAudit} disabled={starting}
                data-testid="dashboard-rerun-audit-btn">
                {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Re-run Readiness Audit
              </Button>}
            </CardContent>
          </Card>
        )}

        {/* 2. Pipeline stepper */}
        {(phase === 'running' || phase === 'complete') && (
          <div className="card-elevated p-4" data-testid="dashboard-stepper">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <ol className="flex flex-wrap gap-x-5 gap-y-2">
                {steps.map((s) => (
                  <li key={s.label} className="flex items-center gap-1.5 text-xs">
                    {s.state === 'complete'
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      : <span className={`inline-block h-2 w-2 rounded-full ${STEP_DOT[s.state]}`} />}
                    <span className={s.state === 'pending' ? 'text-muted-foreground' : 'text-foreground'}>{s.label}</span>
                  </li>
                ))}
              </ol>
              {row?.progress_percent !== null && row?.progress_percent !== undefined && (
                <span className="text-[11px] tabular-nums text-muted-foreground"
                  data-testid="dashboard-progress-pct">
                  {row.progress_percent}% complete
                </span>
              )}
            </div>
            {phase === 'running' && (
              <>
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="dashboard-current-step">
                  {row?.current_step || DASH}
                </p>
                <div className="mt-1 h-1.5 bg-muted rounded overflow-hidden">
                  <div className="h-full bg-blue-500 transition-[width]"
                    style={{ width: `${row?.progress_percent || 0}%` }} />
                </div>
              </>
            )}
          </div>
        )}

        {phase === 'complete' && (
          <>
            {/* 3. Five-card KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="card-elevated p-3 flex flex-col items-center" data-testid="dash-card-score">
                <p className="text-[11px] text-muted-foreground self-start">BEP Readiness</p>
                <ReadinessGauge score={score} band={cr.band} testId="dash-gauge-value" />
                <p className={`text-[11px] text-center ${critCount > 0 ? 'text-[hsl(var(--destructive))] font-medium' : 'text-muted-foreground'}`}
                  data-testid="dash-critical-gaps">
                  {num(critCount)} critical gap{critCount === 1 ? '' : 's'}
                </p>
              </div>
              <div className="card-elevated p-3" data-testid="dash-card-executable">
                <p className="text-[11px] text-muted-foreground">Executable Checks</p>
                <p className="text-2xl font-semibold tabular-nums" data-testid="dash-card-executable-value">
                  {num(m.total_executable_checks)}
                </p>
                <div className="h-1.5 bg-muted rounded overflow-hidden flex mt-1">
                  {m.total_executable_checks > 0 && (
                    <>
                      <div className="h-full bg-green-500"
                        style={{ width: `${((m.enforceable_rules || 0) / m.total_executable_checks) * 100}%` }} />
                      <div className="h-full bg-amber-500"
                        style={{ width: `${((m.conditional_rules || 0) / m.total_executable_checks) * 100}%` }} />
                    </>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="dash-executable-reconciliation">
                  {num(m.enforceable_rules)} enforceable + {num(m.conditional_rules)} conditional
                  = {num(m.total_executable_checks)}
                </p>
              </div>
              <div className="card-elevated p-3" data-testid="dash-card-element">
                <p className="text-[11px] text-muted-foreground">Element-Level</p>
                <p className="text-2xl font-semibold tabular-nums" data-testid="dash-card-element-value">
                  {num(m.element_level_checks)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">per-element checks</p>
              </div>
              <div className="card-elevated p-3" data-testid="dash-card-preflight">
                <p className="text-[11px] text-muted-foreground">File/Model Preflight</p>
                <p className="text-2xl font-semibold tabular-nums" data-testid="dash-card-preflight-value">
                  {num(m.file_model_preflight_checks)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">naming, schema, units</p>
                <p className="text-[11px] text-muted-foreground" data-testid="dash-preflight-reconciliation">
                  {num(m.element_level_checks)} element + {num(m.file_model_preflight_checks)} preflight
                  = {num(m.total_executable_checks)} executable
                </p>
              </div>
              <div className="card-elevated p-3 border border-[hsl(var(--destructive))]/40"
                data-testid="dash-card-blocked">
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <FileWarning className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" /> Blocked Families
                </p>
                <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--destructive))]"
                  data-testid="dash-blocked-count">
                  {num(m.blocked_requirement_families)}
                </p>
                <p className="text-[11px] text-[hsl(var(--destructive))] font-medium mt-1">
                  BEP remediation required
                </p>
                <p className="text-[11px] text-muted-foreground" data-testid="dash-blocked-split">
                  {num(m.blocked_source_gaps)} source · {num(m.blocked_engine_gaps)} engine
                </p>
              </div>
            </div>

            {/* 4. Two-column band */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
              <Card className="card-elevated" data-testid="dash-readiness-profile">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-accent" /> 20-Point Readiness Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {groupRows.map((g, i) => (
                    <div key={g.label} className="space-y-0.5" data-testid={`dash-group-bar-${i}`}>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-muted-foreground">{g.label}</span>
                        <span className="tabular-nums font-medium">
                          {g.pct === null ? DASH : `${g.pct.toFixed(0)}%`}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${g.pct || 0}%` }} />
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 flex-wrap pt-1" data-testid="dash-status-chips">
                    <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-700 border-green-500/30">
                      {statusCounts.machine_actionable} machine-actionable
                    </Badge>
                    <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-700 border-blue-500/30">
                      {statusCounts.defined} defined
                    </Badge>
                    <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-500/30">
                      {statusCounts.mentioned} mentioned
                    </Badge>
                    <Badge variant="outline" className="text-[10px] bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                      {statusCounts.missing} missing
                    </Badge>
                  </div>
                  <button className="text-xs text-accent hover:underline inline-flex items-center gap-1"
                    onClick={() => setShowAllCriteria(!showAllCriteria)}
                    aria-expanded={showAllCriteria}
                    data-testid="dash-view-all-criteria">
                    {showAllCriteria ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    View all {criteria.length || DASH} criteria
                  </button>
                  {showAllCriteria && (
                    <div ref={criteriaTableRef}>
                      <table className="w-full text-xs" data-testid="dash-checklist">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b border-border">
                            <th className="py-1.5 pr-2">ID</th>
                            <th className="py-1.5 pr-2">Criterion</th>
                            <th className="py-1.5 pr-2">Score</th>
                            <th className="py-1.5 pr-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {criteria.map((c) => <ChecklistRow key={c.criterion_id} c={c} />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-3">
                <Card className="card-elevated" data-testid="dash-what-bep-tells">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">What this BEP tells us</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {strengthRows.length === 0 && weakest.length === 0 && (
                      <p className="text-[11px] text-muted-foreground">{DASH}</p>
                    )}
                    {strengthRows.map((s) => (
                      <p key={s.criterion_id} className="text-[11px] flex items-start gap-1.5"
                        data-testid={`dash-strength-${s.criterion_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600" />
                        <span>
                          <span className="font-medium">{s.name}</span>
                          {s.note && <span className="text-muted-foreground"> — {s.note}</span>}
                        </span>
                      </p>
                    ))}
                    {weakest.map((c) => (
                      <p key={c.criterion_id} className="text-[11px] flex items-start gap-1.5"
                        data-testid={`dash-warning-${c.criterion_id}`}>
                        {c.score === 0
                          ? <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--destructive))]" />
                          : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />}
                        <span>
                          <span className="font-medium">{c.name}</span>
                          {(c.gaps || [])[0] && <span className="text-muted-foreground"> — {c.gaps[0]}</span>}
                        </span>
                      </p>
                    ))}
                  </CardContent>
                </Card>
                <Card className="card-elevated" data-testid="dash-disposition">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Requirement disposition</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <CountBar label="Enforce" count={clsCounts.ENFORCE} total={fams.length}
                      color="bg-green-500" testId="dash-bar-enforce" />
                    <CountBar label="Conditional" count={clsCounts.CONDITIONAL} total={fams.length}
                      color="bg-amber-500" testId="dash-bar-conditional" />
                    <CountBar label="Blocked" count={clsCounts.HOLD_SOURCE_GAP} total={fams.length}
                      color="bg-[hsl(var(--destructive))]" testId="dash-bar-blocked" />
                    <CountBar label="Process-only" count={clsCounts.PROCESS_ONLY} total={fams.length}
                      color="bg-muted-foreground/60" testId="dash-bar-process" />
                    {clsCounts.NARRATIVE > 0 && (
                      <CountBar label="Narrative" count={clsCounts.NARRATIVE} total={fams.length}
                        color="bg-muted-foreground/40" testId="dash-bar-narrative" />
                    )}
                    <p className="text-[11px] text-muted-foreground pt-1" data-testid="dash-total-requirements">
                      Total requirements: {fams.length || DASH}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* 5. Priority Remediation */}
            {remediations.length > 0 && (
              <Card className="card-elevated" data-testid="dash-remediation">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Priority Remediation</CardTitle>
                  <CardDescription className="text-[11px]">
                    Click a row to inspect its evidence below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="py-1.5 pr-2">Priority</th>
                        <th className="py-1.5 pr-2">Requirement family</th>
                        <th className="py-1.5 pr-2">Why blocked</th>
                        <th className="py-1.5 pr-2">Evidence pages</th>
                        <th className="py-1.5 pr-2">Remediation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {remediations.map((r) => {
                        const crit = criteria.find((c) => c.criterion_id === r.criterion_id);
                        const pages = [...new Set((crit?.evidence || [])
                          .map((e) => e.page).filter((p) => p !== null && p !== undefined))];
                        const p1 = r.score === 0;
                        const selected = activeRemId === r.criterion_id;
                        return (
                          <tr key={r.criterion_id}
                            className={`border-b border-border/50 align-top cursor-pointer hover:bg-muted/30 ${selected ? 'bg-muted/40' : ''}`}
                            onClick={() => setSelectedRem(r.criterion_id)}
                            data-testid={`dash-remediation-row-${r.criterion_id}`}>
                            <td className="py-1.5 pr-2 whitespace-nowrap">
                              <span className={`inline-block border rounded px-1.5 text-[11px] font-medium ${
                                p1
                                  ? 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30'
                                  : 'bg-amber-500/10 text-amber-700 border-amber-500/30'
                              }`} data-testid={`dash-remediation-priority-${r.criterion_id}`}>
                                {p1 ? 'P1' : 'P2'}
                              </span>
                              {r.critical && (
                                <Badge variant="outline"
                                  className="ml-1 text-[10px] px-1 py-0 border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))]">
                                  CRITICAL
                                </Badge>
                              )}
                            </td>
                            <td className="py-1.5 pr-2 font-medium">{r.name || r.criterion_id}</td>
                            <td className="py-1.5 pr-2 text-muted-foreground">
                              {(r.gaps || []).slice(0, 2).map((g, i) => <p key={i}>• {g}</p>)}
                            </td>
                            <td className="py-1.5 pr-2 tabular-nums">{pages.length ? pages.join(', ') : DASH}</td>
                            <td className="py-1.5 pr-2 text-muted-foreground">
                              {(r.remediation || []).map((x, i) => <p key={i}>• {x}</p>)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Evidence & Traceability (selected remediation row) */}
            <Card className="card-elevated" data-testid="dash-evidence">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Evidence & Traceability</CardTitle>
                <CardDescription className="text-[11px]">
                  Every finding keeps page, block and evidence.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {!activeRem && <p className="text-[11px] text-muted-foreground">{DASH}</p>}
                {activeRem && (
                  <div data-testid={`dash-evidence-${activeRem.criterion_id}`}>
                    <p className="text-xs font-medium mb-1">
                      {activeRem.criterion_id} — {activeRem.name || DASH}
                    </p>
                    {(activeCrit?.evidence || []).length === 0 && (
                      <p className="text-[11px] text-muted-foreground">
                        No evidence recorded for this criterion.
                      </p>
                    )}
                    {(activeCrit?.evidence || []).map((e, i) => (
                      <p key={i} className="text-[11px] text-muted-foreground">
                        p.{num(e.page)} · <span className="font-mono">{e.block_id || DASH}</span> — “{e.text || DASH}”
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 7. Footer action bar */}
            <div className="card-elevated p-4 flex items-center justify-between gap-3 flex-wrap"
              data-testid="dash-footer">
              <p className="text-xs text-muted-foreground">
                The BEP audit is complete. Review critical gaps or continue with rule extraction.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm"
                  onClick={downloadRemediationReport}
                  disabled={downloading}
                  title="Download the readiness audit as a PDF"
                  data-testid="dash-download-report-btn">
                  {downloading
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Preparing PDF…</>
                    : <><Download className="h-3.5 w-3.5 mr-1" /> Download BIM Compliance & Advisory Report</>}
                </Button>
                <Button variant="outline" size="sm" onClick={reviewCriteria}
                  data-testid="dash-review-criteria-btn">
                  <ListChecks className="h-3.5 w-3.5 mr-1" /> Review 20 Criteria
                </Button>
                <Button size="sm" onClick={() => navigate(`/projects/${projectId}${isReadOnly ? '' : '#bep-step1-anchor'}`)}
                  data-testid="dash-continue-extract-btn">
                  {isReadOnly ? 'Back to Project' : 'Continue to Extract Rules'} <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
            {downloadError && (
              <p className="text-xs text-[hsl(var(--destructive))] mt-1 text-right"
                data-testid="dash-download-error">{downloadError}</p>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
